import { REGISTRATION_MODALS } from '@nerdwallet/js-auth-tools/dist/registration/constants';
import type { RegistrationModalType } from '@nerdwallet/js-auth-tools/dist/registration/constants';

import {
  IGNORABLE_V1,
  IGNORABLE_V3_INVESTING,
  IGNORABLE_V3_MORTGAGES,
  IGNORABLE_V3_RETIREMENT,
  IGNORABLE_V3_RIGHT_MOVE,
} from './analytics.constants';
import { REDIRECT_TO_HOME_TAXES_EXPERIMENT_URLS } from './constants';

type Variant = {
  id: string;
  regFlow?: RegistrationModalType;
  analyticsBase?: any;
  flow?: string;
};

export type IncludeExcludeConfigProps = {
  contentTypesToInclude?: string[];
  verticalsToInclude?: string[];
  topicsToInclude?: string[];
  urlsToInclude?: string[];
  urlMatch?: RegExp;
};

export type ExperimentConfig = {
  /**
   * ID of the experiment in Optimizely.
   * Recommended id format: {jira-1234}_{my-test-name}_experiment
   */
  id: string;
  variants: {
    /**
     * Control variant should be named "control", but in any case, should match your Optimizely config.
     */
    CONTROL: Variant;
    VARIANT?: Variant;
  };
  includeExcludeConfig?: IncludeExcludeConfigProps;
};

export const EXPERIMENTS: Record<string, ExperimentConfig> = {
  brandedInvestingContextualIgnorableModal: {
    id: 'reg-1846-branded-contextual-ign-val-prop-investing-urls',
    variants: {
      CONTROL: {
        id: 'control',
        regFlow:
          REGISTRATION_MODALS.BRANDED_INVESTING_CONTEXTUAL_IGNORABLE_MODAL as RegistrationModalType,
        analyticsBase: IGNORABLE_V3_INVESTING,
      },
    },
    includeExcludeConfig: {
      verticalsToInclude: ['Investing'],
    },
  },
  compoundInterestCalculatorInvestingContextualIgnorable: {
    id: 'cad-697_cic_contextual_reg_investing',
    variants: {
      CONTROL: {
        id: 'control',
        regFlow: REGISTRATION_MODALS.IGNORABLE_MODAL as RegistrationModalType,
        analyticsBase: IGNORABLE_V1,
      },
      VARIANT: {
        id: 'variant',
        regFlow:
          REGISTRATION_MODALS.BRANDED_INVESTING_CONTEXTUAL_IGNORABLE_MODAL as RegistrationModalType,
        analyticsBase: IGNORABLE_V3_INVESTING,
      },
    },
    includeExcludeConfig: {
      urlsToInclude: ['/calculator/compound-interest-calculator'],
    },
  },
  sccRetirementCalculatorContextualIgnorable: {
    id: 'cad-749_contextual_reg_retirement',
    variants: {
      CONTROL: {
        id: 'control',
        regFlow: REGISTRATION_MODALS.IGNORABLE_MODAL as RegistrationModalType,
        analyticsBase: IGNORABLE_V1,
      },
      VARIANT: {
        id: 'variant',
        regFlow:
          REGISTRATION_MODALS.RETIREMENT_CONTEXTUAL_IGNORABLE_MODAL as RegistrationModalType,
        analyticsBase: IGNORABLE_V3_RETIREMENT,
      },
    },
    includeExcludeConfig: {
      urlsToInclude: ['/calculator/retirement-calculator'],
    },
  },
  otherRetirementCalcsContextualIgnorable: {
    id: 'cad-786_context_reg_retirement_other',
    variants: {
      CONTROL: {
        id: 'control',
        regFlow: REGISTRATION_MODALS.IGNORABLE_MODAL as RegistrationModalType,
        analyticsBase: IGNORABLE_V1,
      },
      VARIANT: {
        id: 'variant',
        regFlow:
          REGISTRATION_MODALS.RETIREMENT_CONTEXTUAL_IGNORABLE_MODAL as RegistrationModalType,
        analyticsBase: IGNORABLE_V3_RETIREMENT,
      },
    },
    includeExcludeConfig: {
      urlsToInclude: [
        '/investing/401k-calculator',
        '/investing/roth-ira-calculator',
      ],
    },
  },
  homeCalculatorsContextualIgnorable: {
    id: 'cad-747_contextual_reg_home',
    variants: {
      CONTROL: {
        id: 'control',
        regFlow: REGISTRATION_MODALS.IGNORABLE_MODAL as RegistrationModalType,
        analyticsBase: IGNORABLE_V1,
      },
      VARIANT: {
        id: 'variant',
        regFlow:
          REGISTRATION_MODALS.SMART_MORTGAGES_CONTEXTUAL_IGNORABLE_MODAL as RegistrationModalType,
        analyticsBase: IGNORABLE_V3_MORTGAGES,
      },
    },
    includeExcludeConfig: {
      urlsToInclude: [
        '/mortgages/how-much-house-can-i-afford',
        '/mortgages/mortgage-calculator',
        '/mortgages/rent-vs-buy-calculator',
        '/mortgages/amortization-schedule-calculator',
        '/mortgages/how-much-house-can-i-afford',
        '/mortgages/how-much-house-can-i-afford/calculate-affordability',
      ],
    },
  },
  colCalculatorContextualIgnorable: {
    id: 'cad-748_contextual_reg_col',
    variants: {
      CONTROL: {
        id: 'control',
        regFlow: REGISTRATION_MODALS.IGNORABLE_MODAL as RegistrationModalType,
        analyticsBase: IGNORABLE_V1,
      },
      VARIANT: {
        id: 'variant',
        regFlow:
          REGISTRATION_MODALS.RIGHT_MOVE_CONTEXTUAL_IGNORABLE_MODAL as RegistrationModalType,
        analyticsBase: IGNORABLE_V3_RIGHT_MOVE,
      },
    },
    includeExcludeConfig: {
      urlsToInclude: ['/cost-of-living-calculator'],
    },
  },
  mobileHeaderSigninDesignTest: {
    id: 'GROW-668_mobileheadernavregsignindesigntest_experiment',
    variants: {
      CONTROL: {
        id: 'control',
      },
      VARIANT: {
        id: 'treatment',
      },
    },
  },
  redirectFromTaxArticlesToHomeTaxes: {
    id: 'bain-1580_redirect_to_home_taxes_from_reg_modules_experiment',
    variants: {
      CONTROL: {
        id: 'control',
        regFlow: REGISTRATION_MODALS.IGNORABLE_MODAL as RegistrationModalType,
        analyticsBase: IGNORABLE_V1,
      },
      VARIANT: {
        id: 'variant',
        regFlow:
          REGISTRATION_MODALS.REDIRECT_TO_MY_TAXES_CONTEXTUAL_IGNORABLE_MODAL as RegistrationModalType,
        analyticsBase: IGNORABLE_V1,
      },
    },
    includeExcludeConfig: {
      urlsToInclude: REDIRECT_TO_HOME_TAXES_EXPERIMENT_URLS,
    },
  },
  // to-do: clean up experiment code to make Auth0 the default experience after full rollout
  auth0: {
    id: 'idplat-auth0-global-nav',
    variants: {
      CONTROL: {
        id: 'control',
      },
      VARIANT: {
        id: 'variant',
      },
    },
  },
  // TODO: Clean up this section in https://nerdwallet.atlassian.net/browse/BAIN-1602
  preSeasonCotextualIgnorable: {
    id: 'bain-1602_pre_season_contenxtual_ignorable_registrations',
    variants: {
      CONTROL: {
        id: 'control',
        regFlow: REGISTRATION_MODALS.IGNORABLE_MODAL as RegistrationModalType,
        analyticsBase: IGNORABLE_V1,
      },
      VARIANT: {
        id: 'with_logo',
        regFlow:
          REGISTRATION_MODALS.PRE_SEASON_REGISTRATION_CONTEXTUAL_IGNORABLE_MODAL as RegistrationModalType,
        analyticsBase: IGNORABLE_V1,
      },
    },
    includeExcludeConfig: {
      urlsToInclude: [
        '/article/taxes/4-good-ways-put-tax-refund-work',
        '/article/taxes/use-your-tax-return-to-map-out-a-better-financial-future',
        '/article/taxes/its-time-to-fix-social-securitys-tax-burden',
        '/article/taxes/moving-to-escape-taxes-make-sure-its-a-clean-break',
        '/article/finance/tax-traps-that-side-hustlers-should-avoid',
        '/article/taxes/tax-filing',
        '/article/taxes/irs-data',
        '/article/taxes/tax-deductions-tax-breaks',
        '/article/taxes/10-tax-forms-to-know-before-file',
        '/article/taxes/unemployment-taxable',
        '/article/taxes/jackson-hewitt-tax-debt-resolution-services-review',
        '/article/taxes/community-tax-review',
        '/article/taxes/irs-scaled-back-functions-coronavirus-how-you-can-adjust',
        '/article/taxes/optima-tax-relief-review',
        '/article/taxes/estate-tax',
        '/article/taxes/avoid-irs-scams',
        '/article/taxes/fsa-hsa-taxes',
        '/article/taxes/qualify-child-child-care-tax-credit',
        '/article/taxes/tax-relief-back-taxes',
        '/article/taxes/amended-tax-return',
        '/article/taxes/11-big-tax-mistakes-avoid',
        '/article/taxes/state-income-tax-rates',
        '/article/taxes/irs-phone-number-list-customer-service',
        '/article/taxes/can-you-take-earned-income-tax-credit',
        '/article/taxes/selling-home-capital-gains-tax',
        '/article/taxes/gift-tax-rate',
        '/article/taxes/1099-misc-form',
        '/article/taxes/tax-lien',
        '/article/taxes/tax-levy',
        '/article/taxes/get-irs-transcript-tax-transcript',
        '/article/taxes/reasons-irs-audit',
        '/article/taxes/how-to-set-up-irs-payment-plan',
        '/article/loans/personal-loans/tax-refund-loans-give-cash-now-to-early-filers',
        '/article/taxes/foreign-tax-credit-taxes-foreign-income',
        '/article/taxes/tax-deadline-tax-day-taxes-due',
        '/article/taxes/smart-ways-to-spend-your-tax-return',
        '/article/taxes/innocent-spouse-relief',
        '/article/taxes/investment-taxes-basics-investors',
        '/article/loans/student-loans/education-tax-credits',
        '/article/taxes/itemized-deductions-standard-deduction',
        '/article/taxes/what-is-w-2-form',
        '/article/taxes/tax-planning',
        '/article/taxes/alternative-minimum-tax-amt',
        '/article/taxes/claim-tax-dependent-rules-qualify',
        '/article/taxes/sales-tax-holiday-back-to-school-shopping',
        '/article/taxes/donor-advised-funds',
        '/article/taxes/federal-income-tax-brackets',
        '/article/taxes/capital-gains-tax-rates',
        '/article/taxes/california-state-tax',
        '/article/taxes/vat-value-added-tax',
        '/article/taxes/property-tax',
        '/article/taxes/irs-free-file-tax-preparation-help',
        '/article/investing/how-much-to-contribute-to-401k',
        '/article/taxes/taxes-on-stocks',
        '/article/taxes/standard-deduction',
        '/article/taxes/self-employment-tax',
        '/article/taxes/inheritance-tax',
        '/article/taxes/tax-deductible-donations-charity',
        '/article/taxes/401k-taxes',
        '/article/taxes/adjusted-gross-income-agi',
        '/article/taxes/form-1040',
        '/article/taxes/property-tax-deduction',
        '/article/taxes/dividend-tax-rate',
        '/article/taxes/schedule-c-definition',
        '/article/taxes/havent-received-w2-take-steps',
        '/article/taxes/tips-save-taxes',
        '/article/taxes/medical-expense-tax-deduction',
        '/article/taxes/sales-tax-deduction',
        '/article/taxes/pay-taxes-game-show-winnings',
        '/article/taxes/what-are-tax-exemptions-and-how-do-they-work',
        '/article/taxes/tax-credit-vs-tax-deduction',
        '/article/taxes/do-i-need-to-file-a-tax-return',
        '/article/taxes/tax-changes',
        '/article/taxes/how-to-find-best-tax-preparer-near',
        '/article/taxes/irs-extends-tax-filing-deadline-to-july-15',
        '/article/taxes/tax-prep-checklist',
        '/article/taxes/schedule-a',
        '/article/taxes/can-you-take-the-savers-credit',
        '/article/taxes/how-to-get-tax-extension-online',
        '/article/taxes/1099-int-form',
        '/article/taxes/dont-miss-tax-extension-deadline',
        '/article/taxes/what-tax-credits-can-i-qualify-for',
        '/article/taxes/taxes-on-mutual-funds',
        '/article/taxes/tax-deductions-credits-in-retirement',
        '/article/taxes/tax-evasion-vs-tax-avoidance',
        '/article/taxes/homeowner-tax-tips',
        '/article/taxes/tax-loss-harvesting',
        '/article/taxes/pay-irs-options',
        '/article/taxes/penalty-for-failure-to-file-taxes',
        '/article/taxes/tax-refund-late-irs-owe-interest',
        '/article/taxes/tax-breaks-for-parents-of-functional-needs-kids',
        '/article/taxes/havent-filed-tax-return-lately-you-can-still-get-refund',
        '/article/taxes/are-tips-taxable',
        '/article/taxes/fastest-tax-refund',
        '/article/taxes/form-1099-div',
        '/article/taxes/6-late-filing-tax-mistakes-need-avoid',
        '/article/taxes/tax-free-income-nontaxable-income',
        '/article/taxes/tax-breaks-caring-for-elderly-parents',
        '/article/taxes/how-long-to-keep-tax-records',
        '/article/taxes/best-time-file-tax-return',
        '/article/taxes/wealth-tax',
        '/article/taxes/tax-refund-stolen',
        '/article/taxes/medical-expenses-marijuana',
        '/article/taxes/cant-pay-income-taxes',
        '/article/taxes/how-irs-knows-if-you-cheat-on-taxes',
        '/article/taxes/irs-penalty-first-time-abatement',
        '/article/taxes/missing-tax-documents',
        '/article/taxes/file-taxes-jointly-separately-return',
        '/article/taxes/withdrawing-from-529-plan-college',
        '/article/taxes/what-happens-if-you-dont-pay-your-taxes-by-deadline',
        '/article/taxes/tax-deadline-postponed-90-days-coronavirus',
        '/article/taxes/filing-taxes-after-divorce',
        '/article/taxes/big-refund-give-yourself-a-raise',
        '/article/taxes/youre-filing-tax-return-late',
        '/article/taxes/need-a-tax-extension-you-might-already-have-one',
        '/article/taxes/harry-meghan-royal-family-welcome-bundle-tax-hassles',
        '/article/taxes/tips-to-cut-cost-of-having-taxes-done',
        '/article/taxes/take-the-headache-out-of-itemizing-your-tax-return',
        '/article/taxes/can-you-get-a-passport-if-you-owe-taxes',
        '/article/taxes/rental-owners-extra-tax-break',
        '/article/taxes/millennials-fear-filing-taxes',
        '/article/taxes/bad-day-tax-break',
        '/article/taxes/tax-breaks-new-college-graduates',
        '/article/taxes/split-tax-refund',
        '/article/taxes/find-good-tax-preparer',
        '/article/taxes/head-of-household-status',
        '/article/taxes/tax-preparer-mistake-on-your-return',
        '/article/taxes/dont-trust-spouse-at-tax-time',
        '/article/taxes/what-to-do-if-irs-breaks-rules',
        '/article/taxes/how-to-choose-tax-filing-status',
        '/article/investing/roth-ira-taxes',
        '/article/taxes/want-a-raise-in-2021-you-may-not-even-need-to-ask-for-it',
        '/article/taxes/irs-offer-in-compromise-basics-and-who-qualifies',
        '/article/taxes/turbotax-review',
        '/article/taxes/hr-block-review',
        '/article/taxes/taxact-review',
        '/article/taxes/taxslayer-review',
        '/article/taxes/track-my-refund',
        '/article/taxes/taxact-vs-turbotax',
        '/article/taxes/hr-block-vs-turbotax',
        '/article/taxes/best-tax-software',
        '/article/taxes/new-york-state-tax',
        '/article/investing/click-to-call',
        '/article/taxes/5-ways-to-trim-your-tax-prep-costs-this-year',
        '/article/taxes/ip-pin',
        '/article/taxes/3-ways-to-get-socially-distanced-tax-prep-this-year',
        '/article/credit-cards/unwelcome-tax-surprises-may-await-those-with-debt',
        '/article/taxes/5-sneaky-things-covid-19-might-do-to-your-tax-bill',
        '/article/taxes/options-pay-tax-bill',
        '/article/taxes/tax-attorney',
        '/article/taxes/income-taxes/tax-refund-prepaid-debit-card',
        '/article/taxes/americans-dont-know-much-about-taxes-or-that-they-might-get-them-done-for-free',
        '/article/taxes/americans-missing-out-on-free-tax-software-dont-know-basic-irs-facts',
        '/article/taxes/taxes-in-retirement',
        '/article/taxes/virginia-state-tax',
        '/article/taxes/arizona-state-income-tax-rates',
        '/article/taxes/cant-pay-tax-bill',
        '/article/taxes/withholding-tax',
        '/article/taxes/rental-property-tax-deductions',
        '/article/taxes/529-tax-deduction-k-12-school',
        '/article/taxes/why-we-no-longer-fear-april-15',
        '/article/taxes/states-with-no-income-tax',
        '/article/taxes/irs-id-me-facial-recognition-scan',
        '/article/taxes/2021-tax-surprises',
        '/article/taxes/energy-tax-credits',
        '/article/taxes/data-taxpayers-freefile',
        '/article/taxes/bonus-tax-rate-how-are-bonuses-taxed',
        '/article/taxes/gas-taxes',
        '/article/taxes/ev-tax-credit-electric-vehicle-tax-credit',
        '/article/investing/social-security/do-self-employed-workers-pay-social-security-taxes',
        '/article/taxes/income-taxes/inflation-impact-taxes',
        '/article/taxes/year-end-tax-strategies',
        '/article/taxes/mortgage-interest-rate-deduction',
        '/article/taxes/nerdwallet-2023-tax-report',
        '/article/taxes/wheres-my-amended-tax-return',
        '/article/taxes/data-taxpayers-freefile-2023',
        '/article/taxes/w-9',
        '/article/taxes/nerdwallet-tax-report-2022',
        '/article/taxes/child-and-dependent-care-tax-credit',
        '/article/taxes/section-179-deduction',
        '/article/taxes/dont-miss-these-3-tax-credits-when-you-file-this-year',
        '/article/taxes/sports-betting-taxes',
        '/article/taxes/venmo-taxes',
        '/article/taxes/are-gofundme-donations-taxable-tax-tips-for-crowdfunding',
        '/article/taxes/solar-tax-credit',
        '/article/taxes/zelle-taxes',
        '/article/taxes/georgia-state-tax',
        '/article/taxes/salt-tax-deduction',
        '/article/taxes/ohio-state-tax',
        '/article/taxes/florida-taxes',
        '/article/taxes/illinois-tax',
        '/article/taxes/colorado-state-income-tax-rates',
        '/article/taxes/back-taxes-past-due-returns',
        '/article/taxes/free-tax-filing-how-to-file-taxes-for-free',
        '/article/taxes/form-4868-tax-extension-form',
        '/article/taxes/massachusetts-state-tax-rates',
        '/article/taxes/cpa-near-me',
        '/article/taxes/certified-public-accountant-what-is-a-cpa',
        '/article/taxes/lottery-tax-calculator',
        '/article/taxes/1099-b-form',
        '/taxes/tax-calculator',
      ],
    },
  },
};

/**
 * The order of these experiments in this array is important as
 * they will be used in the useContextualIgnorableModal hook to determine
 * which modal to configure. If multiple experiments are eligible the
 * experiment closest to the end will be used.
 */
export const CONTEXTUAL_IGNORABLE_MODAL_EXPERIMENTS = [
  EXPERIMENTS.brandedInvestingContextualIgnorableModal,
  EXPERIMENTS.sccRetirementCalculatorContextualIgnorable,
  EXPERIMENTS.otherRetirementCalcsContextualIgnorable,
  EXPERIMENTS.compoundInterestCalculatorInvestingContextualIgnorable,
  EXPERIMENTS.homeCalculatorsContextualIgnorable,
  EXPERIMENTS.colCalculatorContextualIgnorable,
  EXPERIMENTS.redirectFromTaxArticlesToHomeTaxes,
  /* TODO: Clean up this section in https://nerdwallet.atlassian.net/browse/BAIN-1602 */
  EXPERIMENTS.preSeasonCotextualIgnorable,
];

/**
 * Experiments that will be fetched in fetch-async-props.js
 * These experiments should be accessed with useStoredExperiment
 */
export const EXPERIMENT_IDS_TO_FETCH = [
  EXPERIMENTS.mobileHeaderSigninDesignTest.id,
  EXPERIMENTS.sccRetirementCalculatorContextualIgnorable.id,
  EXPERIMENTS.otherRetirementCalcsContextualIgnorable.id,
  EXPERIMENTS.compoundInterestCalculatorInvestingContextualIgnorable.id,
  EXPERIMENTS.homeCalculatorsContextualIgnorable.id,
  EXPERIMENTS.colCalculatorContextualIgnorable.id,
  EXPERIMENTS.auth0.id,
  EXPERIMENTS.redirectFromTaxArticlesToHomeTaxes.id,
  /* TODO: Clean up this section in https://nerdwallet.atlassian.net/browse/BAIN-1602 */
  EXPERIMENTS.preSeasonCotextualIgnorable.id,
];
